import React from "react";
import { css, cx } from "@emotion/css";
// eslint-disable-next-line no-restricted-imports -- The sheet needs its own fixed light theme.
import { ThemeProvider } from "@mui/material/styles";
import { createMuiTheme } from "theme";
import type {
  Currency,
  QuoteDocumentAddress,
  QuoteDocumentContent,
  QuoteDocumentCustomerInformation,
  QuoteDocumentTermsBullet,
} from "client/api-v2";
import { LightTheme } from "components/Theme/Themes";
import logoOctopusDeploy from "resources/images/svgs/logo_octopus_deploy.svg";
import { quoteDocumentColors, quoteDocumentColumns, quoteDocumentLayout } from "./quoteDocumentStyles";

export const quoteSheetClassName = "quote-sheet";

const documentTheme = createMuiTheme("light", LightTheme);

const cellPadding = `${quoteDocumentLayout.cellPadding} ${quoteDocumentLayout.cellSidePadding}`;

const sheetStyles = css({
  backgroundColor: quoteDocumentColors.paper,
  color: quoteDocumentColors.ink,
  width: quoteDocumentLayout.pageWidth,
  maxWidth: "100%",
  margin: "0 auto",
  padding: quoteDocumentLayout.pageMargin,
  boxSizing: "border-box",
  boxShadow: `0 1px 4px ${quoteDocumentColors.screenShadow}`,
  fontFamily: '"Inter", Roboto, Helvetica, Arial, sans-serif',
  fontSize: quoteDocumentLayout.bodyFontSize,
  lineHeight: quoteDocumentLayout.lineHeight,

  // Why: `text.less` colours these elements from `var(--primaryText)`, which is near-white in dark
  // mode, and an element rule beats an inherited colour, so the sheet sets the ink on each.
  "& p, & ul, & ol, & li, & h1, & h2, & h3, & h4, & h5, & h6": { color: quoteDocumentColors.ink },
  "& a": { color: quoteDocumentColors.link, textDecoration: "underline" },

  "& table": { width: "100%", borderCollapse: "collapse", tableLayout: "fixed" },
  "& td, & th": { padding: cellPadding, verticalAlign: "top", textAlign: "left" },
});

const headerStyles = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "18pt",
  flexWrap: "wrap",
  "& img": { height: "auto", width: "auto", maxHeight: quoteDocumentLayout.logoHeight, maxWidth: "100%", minWidth: 0 },
});

const titleStyles = css({ fontSize: quoteDocumentLayout.titleFontSize, lineHeight: 1, marginLeft: "auto" });

const bandStyles = css({
  fontSize: quoteDocumentLayout.bandFontSize,
  "& td": { padding: `1pt ${quoteDocumentLayout.cellSidePadding}` },
});

const subHeadingStyles = css({
  fontSize: quoteDocumentLayout.subHeadingFontSize,
  fontWeight: 700,
  margin: "14pt 0 4pt",
});

const headingStyles = css({
  fontSize: quoteDocumentLayout.headingFontSize,
  fontWeight: 700,
  margin: "18pt 0 6pt",
});

const paragraphStyles = css({ margin: "0 0 4pt" });

const acceptanceStatementStyles = css({ margin: "10pt 0 0" });

const bulletListStyles = css({
  // Why: `base.less` sets `list-style-type: none` on every `ul` and `ol`.
  listStyleType: "disc",
  margin: "0 0 0 18pt",
  padding: 0,
  "& li": { marginBottom: "4pt" },
});

const tableHeaderRowStyles = css({
  backgroundColor: quoteDocumentColors.tableHeader,
  fontWeight: 700,
  WebkitPrintColorAdjust: "exact",
  printColorAdjust: "exact",
});

const lastPricedRowStyles = css({
  "& td": { borderBottom: `1.5pt solid ${quoteDocumentColors.rule}` },
});

const ruledCellStyles = css({ borderBottom: `1pt solid ${quoteDocumentColors.rule}` });

const formRowStyles = css({ "& td": { height: "28pt", verticalAlign: "bottom" } });

// Why: `&&` doubles the class to specificity 0,2,0, so it beats the 0,1,1 `td`/`th` text-align
// rule in `sheetStyles` whatever order emotion injects them in.
const rightStyles = css({ "&&": { textAlign: "right" } });

const boldStyles = css({ fontWeight: 700 });

const caveatStyles = css({
  fontSize: quoteDocumentLayout.caveatFontSize,
  margin: "4pt 0 0",
});

function formatDocumentMoney(amount: number, currency: Currency): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency, currencyDisplay: "code" }).format(amount);
}

function addressLines(address: QuoteDocumentAddress): string[] {
  const locality = [address.city, address.state, address.postalCode].filter(Boolean).join(" ");

  return [address.address1, address.address2, locality || undefined, address.countryName].filter(
    (line): line is string => Boolean(line)
  );
}

function HeaderBand({ content }: { content: QuoteDocumentContent }) {
  const customer: { text: string; isBold?: boolean }[] = [];
  if (content.billTo) {
    customer.push({ text: content.billTo.legalName, isBold: true });
    if (content.attentionName) {
      customer.push({ text: `Attention: ${content.attentionName}` });
    }
    if (content.billTo.address) {
      addressLines(content.billTo.address).forEach((line) => customer.push({ text: line }));
    }
  }

  if (content.reseller) {
    customer.push({ text: `Reseller: ${content.reseller.legalName}`, isBold: true });
    if (content.reseller.address) {
      addressLines(content.reseller.address).forEach((line) => customer.push({ text: line }));
    }
  }

  const facts: { label: string; value: string }[] = [{ label: "Quote Date", value: content.quoteDateLabel }];
  if (content.validUntilLabel) {
    facts.push({ label: "Valid Until", value: content.validUntilLabel });
  }
  facts.push(
    { label: "Quote Number", value: content.quoteNumber },
    { label: "Payment Term", value: content.paymentTermLabel },
    { label: "License Term", value: content.licenceTermLabel },
    { label: "Billing Schedule", value: content.billingScheduleLabel }
  );
  if (content.salesRepresentative) {
    facts.push({ label: "Sales Rep", value: content.salesRepresentative });
  }
  if (content.fulfillmentContactLabel) {
    facts.push({ label: "Licence To", value: content.fulfillmentContactLabel });
  }

  const seller: string[] = [
    content.seller.legalName,
    `${content.seller.taxIdLabel}: ${content.seller.taxId}`,
    `Phone: ${content.seller.phone}`,
    ...content.seller.addressLines,
  ];

  const rowCount = Math.max(customer.length, facts.length, seller.length);

  return (
    <table className={bandStyles}>
      <colgroup>
        {quoteDocumentColumns.band.map((width) => (
          <col key={width} style={{ width }} />
        ))}
      </colgroup>
      <tbody>
        {Array.from({ length: rowCount }, (_, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr key={index}>
            <td className={index === 0 || customer[index]?.isBold ? boldStyles : undefined}>{customer[index]?.text}</td>
            <td>{facts[index] && `${facts[index].label}:`}</td>
            <td>{facts[index]?.value}</td>
            <td className={cx(rightStyles, { [boldStyles]: index === 0 })}>{seller[index]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PriceTable({ content }: { content: QuoteDocumentContent }) {
  const { price, productName } = content;
  const money = (amount: number) => formatDocumentMoney(amount, price.currency);

  const rows = [
    ...price.lines.map((line) => ({
      key: `line-${line.vector}`,
      description: line.description,
      amount: line.lineTotal,
    })),
    ...price.fixedFees.map((fee) => ({
      key: `fee-${fee.description}`,
      description: fee.description,
      amount: fee.amount,
    })),
  ];

  const roundedDown = price.overallDiscount > 0;

  return (
    <>
      <table>
        <colgroup>
          {quoteDocumentColumns.price.map((width) => (
            <col key={width} style={{ width }} />
          ))}
        </colgroup>
        <thead>
          <tr className={tableHeaderRowStyles}>
            <th>Product</th>
            <th>Description</th>
            <th className={rightStyles}>Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.key} className={!roundedDown && index === rows.length - 1 ? lastPricedRowStyles : undefined}>
              <td>{productName}</td>
              <td>{row.description}</td>
              <td className={rightStyles}>{money(row.amount)}</td>
            </tr>
          ))}
          {roundedDown && (
            <>
              <tr>
                <td />
                <td className={rightStyles}>Subtotal</td>
                <td className={rightStyles}>{money(price.subtotal)}</td>
              </tr>
              <tr className={lastPricedRowStyles}>
                <td />
                <td className={rightStyles}>Overall discount</td>
                <td className={rightStyles}>{`-${money(price.overallDiscount)}`}</td>
              </tr>
            </>
          )}
          <tr>
            <td />
            <td className={cx(rightStyles, boldStyles)}>TOTAL:</td>
            <td className={rightStyles}>{money(price.total)}</td>
          </tr>
          {price.tax && (
            <>
              <tr>
                <td />
                <td className={rightStyles}>Estimated tax</td>
                <td className={rightStyles}>{money(price.tax.estimatedTaxAmount)}</td>
              </tr>
              <tr>
                <td />
                <td className={rightStyles}>Estimated total including tax</td>
                <td className={rightStyles}>{money(price.tax.estimatedTotalIncludingTax)}</td>
              </tr>
            </>
          )}
        </tbody>
      </table>
      {price.tax && <p className={caveatStyles}>{price.tax.caveat}</p>}
    </>
  );
}

function TermsBullet({ bullet }: { bullet: QuoteDocumentTermsBullet }) {
  return (
    <li>
      {bullet.textBefore}
      {bullet.linkText && bullet.linkUrl && (
        <a href={bullet.linkUrl} target="_blank" rel="noreferrer">
          {bullet.linkText}
        </a>
      )}
      {bullet.textAfter}
    </li>
  );
}

function SignatureTable() {
  return (
    <table>
      <colgroup>
        {quoteDocumentColumns.signature.map((width) => (
          <col key={width} style={{ width }} />
        ))}
      </colgroup>
      <tbody>
        {[
          ["Signature:", "Effective Date:"],
          ["Name (Print):", "Title:"],
        ].map(([left, right]) => (
          <tr key={left} className={formRowStyles}>
            <td>{left}</td>
            <td className={ruledCellStyles} />
            <td>{right}</td>
            <td className={ruledCellStyles} />
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CustomerInformationTable({ information }: { information: QuoteDocumentCustomerInformation }) {
  const rows: { label: string; value?: string | null }[] = [
    { label: "Purchase Order #:", value: information.purchaseOrderNumber },
    { label: "VAT #:", value: information.taxId },
    { label: "Tax Exemption #:", value: information.taxExemptionCertificateId },
    { label: "Billing Portal Name:" },
    { label: "Billing Contact Name:", value: information.billingContactName },
    { label: "Billing Contact Email:", value: information.billingContactEmail },
  ];

  return (
    <table>
      <colgroup>
        {quoteDocumentColumns.customerInformation.map((width) => (
          <col key={width} style={{ width }} />
        ))}
      </colgroup>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label} className={formRowStyles}>
            <td>{row.label}</td>
            {row.value ? <td>{row.value}</td> : <td className={ruledCellStyles} />}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function QuoteSheet({ content }: { content: QuoteDocumentContent }) {
  return (
    <ThemeProvider theme={documentTheme}>
      <article
        aria-label={`${content.title.toLowerCase()} document`}
        className={`${quoteSheetClassName} ${sheetStyles}`}
      >
        <div className={headerStyles}>
          <img src={logoOctopusDeploy} alt="Octopus Deploy" />
          <div className={titleStyles}>{content.title}</div>
        </div>

        <HeaderBand content={content} />

        <div className={subHeadingStyles}>Your Octopus Subscription</div>
        <p className={paragraphStyles}>{content.subscriptionSummary}</p>

        <div className={subHeadingStyles}>{content.priceSectionHeading}</div>
        <PriceTable content={content} />

        <div className={subHeadingStyles}>{content.renewalPricingHeading}</div>
        {content.renewalPricingParagraphs.map((paragraph) => (
          <p key={paragraph} className={paragraphStyles}>
            {paragraph}
          </p>
        ))}

        <div className={headingStyles}>Terms and Conditions</div>
        <ul className={bulletListStyles}>
          {content.termsAndConditions.map((bullet) => (
            <TermsBullet key={bullet.textBefore} bullet={bullet} />
          ))}
        </ul>

        {content.asksForSignature && (
          <>
            <div className={headingStyles}>Customer Signature</div>
            {content.acceptance ? (
              <table>
                <colgroup>
                  {quoteDocumentColumns.signature.map((width) => (
                    <col key={width} style={{ width }} />
                  ))}
                </colgroup>
                <tbody>
                  <tr>
                    <td>Accepted By:</td>
                    <td>{content.acceptance.acceptedByName}</td>
                    <td>Company:</td>
                    <td>{content.acceptance.acceptedByCompany}</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <SignatureTable />
            )}

            <div className={headingStyles}>Octopus Signature</div>
            <SignatureTable />
          </>
        )}
        <p className={acceptanceStatementStyles}>{content.acceptanceStatement}</p>

        <div className={headingStyles}>Customer Information</div>
        <CustomerInformationTable information={content.customerInformation} />
      </article>
    </ThemeProvider>
  );
}
